import { FingerprintPattern, KeyRound, ShieldCheck, User as UserIcon } from 'lucide-react'
import { useState } from 'react'

import { useCurrentUser } from '../api/auth'
import { formatDate } from '../lib/format'
import { ChangePasswordForm } from './ChangePasswordDialog'
import { Modal } from './Modal'
import { PasskeysForm } from './PasskeysDialog'
import { TwoFactorForm } from './TwoFactorDialog'

type Tab = 'cuenta' | 'password' | 'passkeys' | '2fa'

/** Punto de entrada único a los datos propios y la seguridad de la cuenta
 *  — antes eran 3 diálogos sueltos en el menú de usuario. Cada pestaña
 *  reusa el mismo formulario que su diálogo suelto (`ChangePasswordForm`,
 *  `PasskeysForm`, `TwoFactorForm`, sin su propio `<Modal>` — éste es el
 *  único), así que ninguna lógica de step-up/QR/passkeys está duplicada.
 *
 *  El "Cancelar"/"Listo" de cada formulario embebido llama a `onClose`,
 *  que en el diálogo suelto cierra todo — acá en cambio vuelve a la
 *  pestaña "Cuenta" en vez de cerrar "Mi perfil": es el mismo botón,
 *  redirigido a "volvé a la vista principal de este panel" en vez de
 *  "cerrá el panel entero". El propio cambio de pestaña ya desmonta el
 *  formulario anterior (no vive fuera del `{tab === ... && ...}` que lo
 *  renderiza), así que su estado interno arranca de cero la próxima vez
 *  sin que haga falta resetearlo a mano. */
export function ProfileDialog({ onClose }: { onClose: () => void }) {
  const { data: user } = useCurrentUser()
  const [tab, setTab] = useState<Tab>('cuenta')
  const goToOverview = () => setTab('cuenta')

  return (
    <Modal onClose={onClose}>
      <h3>Mi perfil</h3>
      <div className="profile-dialog">
        <nav className="profile-dialog__tabs" role="tablist" aria-label="Secciones de mi perfil">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'cuenta'}
            className={`profile-dialog__tab${tab === 'cuenta' ? ' is-active' : ''}`}
            onClick={() => setTab('cuenta')}
          >
            <UserIcon size={14} />
            Cuenta
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'password'}
            className={`profile-dialog__tab${tab === 'password' ? ' is-active' : ''}`}
            onClick={() => setTab('password')}
          >
            <KeyRound size={14} />
            Contraseña
          </button>
          {user?.webauthn_available && (
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'passkeys'}
              className={`profile-dialog__tab${tab === 'passkeys' ? ' is-active' : ''}`}
              onClick={() => setTab('passkeys')}
            >
              <FingerprintPattern size={14} />
              Llaves de acceso
            </button>
          )}
          <button
            type="button"
            role="tab"
            aria-selected={tab === '2fa'}
            className={`profile-dialog__tab${tab === '2fa' ? ' is-active' : ''}`}
            onClick={() => setTab('2fa')}
          >
            <ShieldCheck size={14} />
            Verificación en dos pasos
          </button>
        </nav>

        <div className="profile-dialog__panel" role="tabpanel">
          {tab === 'cuenta' && (
            <div className="profile-dialog__account">
              <dl className="profile-dialog__account-list">
                <div>
                  <dt>Email</dt>
                  <dd className="mono">{user?.email}</dd>
                </div>
                <div>
                  <dt>Rol</dt>
                  <dd>{user?.is_admin ? 'Administrador' : 'Usuario'}</dd>
                </div>
                <div>
                  <dt>Cuenta creada</dt>
                  <dd>{formatDate(user?.created_at)}</dd>
                </div>
                <div>
                  <dt>Verificación en dos pasos</dt>
                  <dd>{user?.totp_enabled ? 'Activada' : 'Desactivada'}</dd>
                </div>
              </dl>
              <div className="confirm-dialog__actions">
                <button className="button button--ghost" type="button" onClick={onClose}>
                  Cerrar
                </button>
              </div>
            </div>
          )}
          {tab === 'password' && <ChangePasswordForm onClose={goToOverview} />}
          {tab === 'passkeys' && user?.webauthn_available && (
            <PasskeysForm onClose={goToOverview} />
          )}
          {tab === '2fa' && <TwoFactorForm onClose={goToOverview} />}
        </div>
      </div>
    </Modal>
  )
}
